import { useEffect, useState } from 'react'
import { geo } from '@carrent/platform'

/**
 * Текущее состояние геолокации для показа водителю.
 *
 * Хук только опрашивает позицию и отдаёт её источник — точки на сервер
 * он не шлёт. Отправка трека это отдельная задача с очередью
 * и троттлингом; смешивать их значило бы связать показ состояния
 * с доставкой данных, и при отключённой отправке индикатор врал бы.
 *
 * ── Почему опрос редкий ───────────────────────────────────────────
 *
 * Индикатор отвечает на вопрос «пишется ли моё место», а не «где я».
 * Ответ меняется медленно: выехал из паркинга, потерял сигнал в тоннеле.
 * Частый опрос сажал бы батарею ради строки, которая и так не меняется.
 *
 * ── Почему в фоне не опрашиваем ───────────────────────────────────
 *
 * Браузер всё равно глушит определение позиции, когда вкладка уходит
 * в фон. Таймер, продолжающий тикать, только жёг бы батарею и отдавал
 * устаревший ответ. Возвращаясь на экран, водитель получает свежий
 * опрос — это и есть момент, когда индикатор ему нужен.
 */

const POLL_MS = 60_000

export function useGeoStatus({ enabled = true } = {}) {
  const [position, setPosition] = useState(null)

  useEffect(() => {
    if (!enabled) return undefined

    let alive = true
    let timer = null

    const poll = async () => {
      const next = await geo.getPositionSmart()
      if (alive) setPosition(next)
    }

    const start = () => {
      poll()
      timer = setInterval(poll, POLL_MS)
    }
    const stop = () => {
      clearInterval(timer)
      timer = null
    }

    start()

    // Возврат на экран — момент, когда состояние снова важно
    // и когда браузер снова готов его отдать. Подписка идёт через
    // платформенный слой: прямое обращение к document экранам
    // и хукам водителя запрещено.
    const unsubscribe = geo.onVisibilityChange((visible) => {
      if (!visible) stop()
      else if (!timer) start()
    })

    return () => {
      alive = false
      stop()
      unsubscribe()
    }
  }, [enabled])

  return {
    source: position?.source ?? null,
    accuracy: position?.accuracy ?? null,
    position,
  }
}
